import React, {ReactElement, createRef, RefObject, ReactNode} from 'react';
import {FilterMatchMode} from 'primereact/api';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Calendar} from 'primereact/calendar';
import MComponent from '../../Toolkit/Components/Component';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../../Components/Toolkit/Table';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MButton from '../../Toolkit/Components/Controls/Button';
import {MNewIconName} from '../../Toolkit/Icons';
import {CrewData, CrewDataMultiReply} from '../../Api/ControllerTypes/CrewTypes';
import withRouter from '../../Toolkit/WithRouter';
import {CrewClient} from '../../Api/CrewClient';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import MMultiDropDown from '../../Toolkit/Components/Controls/MultiDropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import MBackButton from '../../Toolkit/Components/Controls/BackButton';
import {AssignCrewRequest, JobAssignmentClient} from '../../Api/JobAssignmentClient';
import {JobAssignmentCount} from '../../Api/ControllerTypes/JobAssignmentTypes';
import EnumContext from '../../Context/enum-context';

type CrewRow = {
    data: CrewData;
}

type RouteParams = {
    startDate: number;
};

type Props = WithMsalProps & WithRouterProps<RouteParams> & {};

/** */
type State = {
    page: MPageProps;
    table: MTableProps<CrewRow>;
    selectedRow?: CrewRow | CrewRow[];
}

class ListCrewsPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_crewClient: NSwagAdapter<CrewClient>;
    private readonly m_jobAssignClient: NSwagAdapter<JobAssignmentClient>;
    private readonly m_pageRef: RefObject<MPage>;
    private m_startDate?: Date;
    private m_endDate?: Date;
    private m_requestId?: string;
    private m_tmTypes?: number[];

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_crewClient = new NSwagAdapter<CrewClient>(new CrewClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_jobAssignClient = new NSwagAdapter<JobAssignmentClient>(new JobAssignmentClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_pageRef = createRef<MPage>();
        this.m_startDate = undefined;
        this.m_endDate = undefined;
        this.m_requestId = '';

        this.initializeState();

    }

    private bindMethods(): void {

        this.isRowSelected = this.isRowSelected.bind(this);
        this.new_Click = this.new_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.getCrewFilterTemplate = this.getCrewFilterTemplate.bind(this);
        this.crewTypeTemplate = this.crewTypeTemplate.bind(this);
        this.assignCrews_Click = this.assignCrews_Click.bind(this);
        this.isPlanner = this.isPlanner.bind(this);

    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<CrewRow> = {
            rowData: [],
            filters: {
                'data.crew.crewName': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.crew.crewType': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.crew.workArea': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                }
            }
        };

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined
        };

    }

    public componentDidMount(): void {

    }

    private isRowSelected(): boolean {
        return this.state.selectedRow === undefined;
    }

    private isAssignPage(): boolean {
        const queryParameters = new URLSearchParams(window.location.search);
        const startDateString = queryParameters.get("startDate");
        const endDateString = queryParameters.get("endDate");
        const requestIdString = queryParameters.get("requestId");

        return startDateString !== null && endDateString !== null && requestIdString !== null;
    }

    private isPlanner(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    /** */
    private async new_Click(): Promise<void> {
        this.props.navigate(`/crews/create`);
    }

    private async assignCrews_Click(): Promise<void> {
        if(this.m_requestId !== null) {
            const assignedCrews = this.state.table.rowData
                                      .filter(r => r.data.crew.assignedJobs.filter(ja => ja.requestId === this.m_requestId!).length > 0)
                                      .map(r => r.data.crew.id);

            const assignRequest = new AssignCrewRequest({
                requestId: this.m_requestId,
                startDate: this.m_startDate,
                endDate: this.m_endDate,
                crewIds: assignedCrews,
                tmTypes: this.m_tmTypes
            });
            const result = await this.m_jobAssignClient
                                     .callWithReply(JobAssignmentCount, (client) => client.assignCrew(assignRequest))
            result.throwIfFailed();
            this.props.history.goBack();
        }
    }

    private table_onSelectionChange(selection: CrewRow | CrewRow[] | undefined): void {
        if(Array.isArray(selection)) {
            const oldSelection = this.state.selectedRow;
            let addedItems = selection;
            if(Array.isArray(oldSelection)) {
                //Compare current item selection with the stored data to filter out removed entries.
                const removedItems = oldSelection.filter(cr => selection.map(cr => cr.data.crew.id).indexOf(cr.data.crew.id) < 0);
                removedItems.forEach((cr) => {
                    const entryIndex = this.state.table.rowData.map(cr => cr.data.crew.id).indexOf(cr.data.crew.id);
                    const valueIndex = this.state.table.rowData[entryIndex].data.crew.assignedJobs.findIndex(ja => ja.requestId === this.m_requestId!);
                    this.safeSetState(draft =>
                        draft.table.rowData[entryIndex].data.crew.assignedJobs.splice(valueIndex)
                    );
                });

                addedItems = selection.filter(cr => oldSelection.map(cr => cr.data.crew.id).indexOf(cr.data.crew.id));
            }
            addedItems.forEach((cr) => {
                const entryIndex = this.state.table.rowData.map(cr => cr.data.crew.id).indexOf(cr.data.crew.id);
                this.safeSetState(draft =>
                    draft.table.rowData[entryIndex].data.crew.assignedJobs.push({requestId: this.m_requestId!, tmTypes: (this.m_tmTypes?.join(',') ?? '')})
                );
            });
            this.safeSetState(draft => draft.selectedRow = selection);
        } else {
            this.safeSetState((draft: State) =>
                draft.selectedRow = selection as CrewRow | undefined);

            if(!this.isAssignPage())
            {
                const navigateTo = selection as CrewRow | undefined
                if(navigateTo !== undefined) {
                    this.props.navigate(`/crews/${navigateTo!.data.crew.id}`);
                }
            }
        }
    }

    private async page_onLoadData(): Promise<void> {
        const queryParameters = new URLSearchParams(window.location.search);
        const startDateString = queryParameters.get("startDate");
        this.m_startDate = startDateString !== null ? new Date(startDateString!) : undefined;
        const endDateString = queryParameters.get("endDate");
        this.m_endDate = endDateString !== null ?  new Date(endDateString!) : undefined;
        this.m_requestId = queryParameters.get("requestId") ?? undefined;
        const tmTypes = queryParameters.get("tmTypes") ?? undefined;
        this.m_tmTypes = tmTypes?.split(",").map(tt => Number(tt)) ?? [];
        
        //Get Row Data
        const listResult = await this.m_crewClient.callWithReply(
            CrewDataMultiReply, (client) => client.getAvailableCrews(this.m_startDate, this.m_endDate, this.isAssignPage()));
        listResult.throwIfFailed();
        const crewList = listResult.reply!.crews.map((w: CrewData) => {
            return {data: w} as CrewRow;
        });

        this.safeSetState((draft: State) => {

            draft.table.rowData = crewList;
            if(this.isAssignPage()) {
                draft.selectedRow = crewList.filter(cr => cr.data.crew.assignedJobs.filter(ja => ja.requestId === this.m_requestId!).length > 0);
            }

        });


    }

    private getToolbarTemplate(): ReactElement<MToolbar> {
        return (
            <MToolbar>
                <MBackButton disabled={false}/>
                <MSpacer/>
                <MButton label="Add"
                         color="success"
                         icon={MNewIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.new_Click}/>
                <MSpacer/>
                {this.isAssignPage() && <React.Fragment>
                    <MButton label={"Assign"}
                             icon={'pi pi-check'}
                             iconOnly={false}
                             disabled={false}
                             onClick={this.assignCrews_Click}
                    />
                    <MSpacer/>
                </React.Fragment>}
            </MToolbar>
        );

    }

    private dateFilterTemplate = (options: any) => {
        return <Calendar value={options.value}
                         onChange={(e: any) => options.filterApplyCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy"
                         placeholder="dd/mm/yyyy"
                         mask="99/99/9999"/>;
    };

    private getCrewFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.crewTypes
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="crewTypeFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private crewTypeTemplate(rowData: CrewRow) {
        const index = this.context.enums?.crewTypes.findIndex(value => value.id === rowData.data.crew.crewType);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.crewTypes[index!].name;
    };

    private occupiedTemplate(rowData: CrewRow) {
        return rowData.data.crew.assignedJobs.length;
    }

    private activeTemplate(rowData: CrewRow) {
        if(rowData.data.crew.active) {
            return "Active";
        }

        return "InActive";
    }

    public render(): ReactElement {
        const dateString = this.m_startDate?.toLocaleDateString() === this.m_endDate?.toLocaleDateString()
                           ? ` for ${this.m_startDate?.toLocaleDateString()}`
                           : ` from ${this.m_startDate?.toLocaleDateString()} to ${this.m_endDate?.toLocaleDateString()}` ;
        const titleString = `Crew${this.m_startDate !== undefined && this.m_endDate !== undefined
                            ? ` Availability ${dateString}`
                            : ''}`;

        let selectedRows = this.state.table.rowData.filter(r => r.data.crew.assignedJobs
                                                                   .filter(ja => ja.requestId === this.m_requestId!).length > 0);
        if((this.m_tmTypes?.length ?? 0) > 0) {
            selectedRows = selectedRows.filter(cr => cr.data.crew.assignedJobs
                                                       .filter(ja =>
                                                           ja.tmTypes.split(',').map(tt => Number(tt))
                                                             .filter(value => this.m_tmTypes?.includes(value)).length > 0
                                                       ).length > 0);
        }

        return (
            <MPage title={titleString}
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MTable
                    rowKey="data.crew.id"
                    rowData={this.state.table.rowData}
                    filters={this.state.table.filters}
                    multipleSelection={this.isAssignPage()}
                    onSelectionChange={this.table_onSelectionChange}
                    selection={selectedRows}
                    sortMode={'multiple'}
                    multiSortMeta={[{field: 'data.crew.crewName', order: 1}]}>

                    <Column field="data.crew.crewName"
                            header="Crew Name"
                            filter={true}
                            sortable={true}
                            style={{width: '20%'}}
                    />
                    <Column field="data.crew.crewType"
                            header="Crew Type"
                            filter={true}
                            filterElement={this.getCrewFilterTemplate}
                            showFilterMenu={false}
                            showClearButton={false}
                            sortable={true}
                            body={this.crewTypeTemplate}
                            style={{width: '30%'}}/>
                    {this.isAssignPage() && <Column field="data.crew.occupiedDays"
                            header="Usage"
                            sortable = {true}
                            body={this.occupiedTemplate}
                            style={{width: '20%'}}/>}
                    <Column field="data.primaryContact.description"
                            header="Contact description"
                            filter={false}
                            sortable={true}
                            style={{width: '30%'}}/>
                    {!this.isPlanner() && <Column field="data.primaryContact.contact"
                            header="Contact"
                            filter={false}
                            sortable={true}
                            style={{width: '20%'}}/>}
                    <Column field="data.crew.active"
                            header={"Status"}
                            filter={false}
                            sortable={true}
                            body={this.activeTemplate}
                            style={{width: '10%'}}/>
                </MTable>
            </MPage>
        );
    }
}

export default withMsal(withRouter(ListCrewsPage));
